import { useId } from "react";
import { Palette } from "lucide-react";
import { useTheme } from "../theme/use-theme";
import type { ColorScheme, Density, DesignDirection } from "../theme/types";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const designs: { value: DesignDirection; label: string; detail: string }[] = [
  { value: "studio", label: "Исследовательская студия", detail: "Сдержанные поверхности и ясная рабочая иерархия" },
  { value: "editorial", label: "Редакционный кабинет", detail: "Тёплая бумага и выразительные заголовки" },
  { value: "calm", label: "Спокойный диалог", detail: "Мягкие формы для участия и общения" },
];

/** Shared, keyboard-accessible appearance preferences for every product shell. */
export function AppearanceMenu() {
  const id = useId();
  const theme = useTheme();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Настройки оформления">
          <Palette aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-labelledby={`${id}-title`}
        className="w-[min(22rem,calc(100vw-2rem))] max-h-[min(42rem,calc(100dvh-5rem))] overflow-y-auto space-y-4"
      >
        <h2 id={`${id}-title`} className="text-base font-semibold">Оформление</h2>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Дизайн</legend>
          {designs.map(({ value, label, detail }) => (
            <label key={value} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border p-2 hover:bg-accent">
              <input
                type="radio" name={`${id}-design`} value={value}
                checked={theme.design === value}
                onChange={() => theme.setDesign(value)}
                className="mt-1 accent-[var(--pulse)]"
              />
              <span><span className="block text-sm font-medium">{label}</span>
                <span className="block text-xs text-muted-foreground">{detail}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Цветовая схема</legend>
          <div className="grid grid-cols-3 gap-1">
            {([ ["light", "Светлая"], ["dark", "Тёмная"], ["system", "Система"] ] as const).map(([value, label]) => (
              <label key={value} className="flex min-h-11 cursor-pointer items-center justify-center gap-1 rounded-md border text-sm hover:bg-accent">
                <input type="radio" name={`${id}-scheme`} value={value}
                  checked={theme.colorScheme === value}
                  onChange={() => theme.setColorScheme(value as ColorScheme)}
                  className="accent-[var(--pulse)]" />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block space-y-2 text-sm font-medium">
          <span>Плотность</span>
          <select value={theme.density} onChange={(event) => theme.setDensity(event.target.value as Density)}
            className="min-h-11 w-full rounded-md border bg-background px-3 text-base text-foreground">
            <option value="compact">Компактная</option>
            <option value="normal">Нормальная</option>
            <option value="spacious">Просторная</option>
          </select>
        </label>
        <div>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
            <input type="checkbox" checked={theme.highContrast} onChange={(event) => theme.setHighContrast(event.target.checked)} className="accent-[var(--pulse)]" />
            Высокий контраст
          </label>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
            <input type="checkbox" checked={theme.reduceMotion} onChange={(event) => theme.setReduceMotion(event.target.checked)} className="accent-[var(--pulse)]" />
            Сниженная анимация
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
